// Below are the components for the JSA Signature Info Tab, will include the ewi employee signatures and the non employee signatures.
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { SignatureModal } from "../../common/SignatureModal";
import { positionSelectStyles } from "../../common/delSelect";
import styles from "./jsaSignatureInfoComponents.module.css";
import { useAppStore } from "../../../store/appStore";

// Helper function to get row error for signature rows
const getSignatureRowError = (autosaveErrors, rowId) => {
  return autosaveErrors?.rowErrors?.[rowId] || null;
};

// Signature modal components that we need to have in the modal when it appears.

// Modal version of employee selection for use inside SignatureModal
const EmployeeSelectionModalInput = ({
  selectedEmployee,
  setSelectedEmployee,
  allEmployees,
  disabled,
}) => {
  const isTouch = useAppStore((s) => s.landingPage?.isTouch);
  const portalTarget =
    typeof document !== "undefined" ? document.body : undefined;
  const touchSelectProps = isTouch
    ? {
        menuPosition: "absolute",
        menuPlacement: "auto",
        menuShouldScrollIntoView: false,
        menuPortalTarget: portalTarget,
        styles: {
          menu: (base) => ({ ...base, zIndex: 10000 }),
          menuPortal: (base) => ({ ...base, zIndex: 10000 }),
        },
      }
    : {
        menuPosition: "fixed",
        menuPortalTarget: portalTarget,
        styles: {
          menu: (base) => ({ ...base, zIndex: 10000 }),
          menuPortal: (base) => ({ ...base, zIndex: 10000 }),
        },
      };

  // Handling selecting an employee
  const handleSelectEmployee = (selected) => {
    setSelectedEmployee({
      id: selected?.value || null,
      name: selected?.label || null,
    });
  };

  return (
    <div className={styles.inputField}>
      <label>Employee</label>
      <Select
        value={
          selectedEmployee?.id
            ? {
                value: selectedEmployee.id,
                label: selectedEmployee.name,
              }
            : null
        }
        onChange={handleSelectEmployee}
        options={allEmployees.map((emp) => ({
          value: emp.employee_id,
          label: emp.label,
        }))}
        isDisabled={disabled}
        styles={{
          ...positionSelectStyles(),
          ...(touchSelectProps.styles || {}),
        }}
        placeholder="Select Employee"
        isClearable={true}
        isSearchable={true}
        menuPortalTarget={touchSelectProps.menuPortalTarget}
        menuPosition={touchSelectProps.menuPosition}
        menuPlacement={touchSelectProps.menuPlacement}
        menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
        className={styles.employeeSelect}
      />
    </div>
  );
};

// Modal input components for non-employee signatures
const NonEmployeeNameInput = ({ name, setName, disabled }) => {
  return (
    <div className={styles.inputField}>
      <label>Name</label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={disabled}
        placeholder="Enter Name"
        maxLength={300}
        className={styles.textInput}
      />
    </div>
  );
};

const NonEmployeeCompanyInput = ({ company, setCompany, disabled }) => {
  return (
    <div className={styles.inputField}>
      <label>Company</label>
      <input
        type="text"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        disabled={disabled}
        placeholder="Enter Company Name"
        maxLength={250}
        className={styles.textInput}
      />
    </div>
  );
};

// Employee Signature Row
const EmployeeSignatureRow = ({ employee, callbacks, onEdit }) => {
  // Get row error for this employee signature
  const rowError = getSignatureRowError(callbacks.autosaveErrors, employee.rowId);
  const employeeIdError = rowError?.employee_id;

  // Handling opening the signature modal with a current employee signature
  const handleSignatureClick = () => {
    onEdit(employee.rowId);
  };

  // Handling removing an employee
  const handleRemoveEmployee = () => {
    callbacks.coreActions.removeJsaSignatureEmployeeRow(
      callbacks.jsaData,
      employee.rowId,
      callbacks.emitPatch
    );
    try {
      // Only ask backend to delete if row was likely persisted
      callbacks?.commit?.deleteSignature?.(callbacks.jsaData, employee.rowId);
    } catch {}
  };

  return (
    <div className={`${styles.signatureCard} ${rowError ? styles.rowError : ""}`} key={employee.rowId}>
      <div className={styles.infoField}>
        <label className={styles.infoLabel}>Employee</label>
        <div className={styles.infoValue}>
          {employee.employeeName || (
            <span className={styles.emptyValue}>No employee selected</span>
          )}
        </div>
        {employeeIdError && <span className={styles.errorText}>{employeeIdError}</span>}
      </div>

      <div
        className={styles.signatureDisplay}
        onClick={
          callbacks.jsaMode !== "view" ? handleSignatureClick : undefined
        }
      >
        {employee.employeeSig ? (
          <img
            src={employee.employeeSig}
            alt="Employee signature"
            className={styles.signatureImage}
          />
        ) : (
          <div className={styles.emptySignature}>
            <span>Click to sign</span>
          </div>
        )}
      </div>

      <div className={styles.signatureCardActions}>
        <button
          className={styles.signButton}
          onClick={handleSignatureClick}
          disabled={callbacks.jsaMode === "view"}
        >
          Edit
        </button>
        <button
          className={styles.deleteButton}
          onClick={handleRemoveEmployee}
          disabled={callbacks.jsaMode === "view"}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

// Employee signature table
const EmployeeSignatureTable = ({ callbacks, modalState, setModalState }) => {
  const disabled = callbacks.jsaMode === "view";

  // Local state for modal inputs
  const [selectedEmployee, setSelectedEmployee] = useState({
    id: null,
    name: null,
  });

  // Get current employee being edited
  const currentEmployee = callbacks.jsaData?.jsaEmployeeSignaturesInfo?.find(
    (emp) => emp.rowId === modalState.currentSignee
  );

  // Update local state when modal opens with employee data
  useEffect(() => {
    if (modalState.signatureModalOpen && currentEmployee) {
      setSelectedEmployee({
        id: currentEmployee.employeeId || null,
        name: currentEmployee.employeeName || null,
      });
    }
  }, [
    modalState.signatureModalOpen,
    modalState.currentSignee,
    currentEmployee,
  ]);

  // Handling auto adding all employees to the employee signature info
  const handleAutoAddEmployees = () => {
    // The action already correctly:
    // 1. Filters out employees that are already in the list
    // 2. Only sends WebSocket patches for genuinely NEW rows
    // No separate commit call needed - it would send patches for ALL rows
    // and wipe out existing signatures for other users via broadcast
    callbacks.coreActions.autoAddAllEmployeesToJsaEmployeeSignatureInfo(
      callbacks.jsaData,
      callbacks.delJsaEmployeeList,
      callbacks.emitPatch
    );
  };

  // Handling adding a new employee signature
  const handleAddEmployeeSignature = () => {
    callbacks.coreActions.addJsaSignatureEmployeeRow(
      callbacks.jsaData,
      callbacks.emitPatch
    );
    const rows = callbacks.jsaData?.jsaEmployeeSignaturesInfo || [];
    const newRow = rows[rows.length - 1];
    if (newRow) {
      // Open modal for new signature
      handleOpenModal(newRow.rowId);
      try {
        // Persist the newly added empty row so it has a server-side identity
        callbacks?.commit?.signatures?.(callbacks.jsaData, [
          {
            id: newRow.rowId,
            employee_id: null,
            employee_sig: null,
          },
        ]);
      } catch {}
    }
  };

  const handleOpenModal = (rowId) => {
    const employee = callbacks.jsaData?.jsaEmployeeSignaturesInfo?.find(
      (emp) => emp.rowId === rowId
    );
    setModalState({
      signatureModalOpen: true,
      currentSignee: rowId,
      currentSignatureImage: employee?.employeeSig || null,
    });
  };

  const handleCloseModal = () => {
    setModalState({
      signatureModalOpen: false,
      currentSignee: null,
      currentSignatureImage: null,
    });
    setSelectedEmployee({ id: null, name: null });
  };

  const handleSave = (signature, signeeRowId) => {
    // Update employee info and signature
    const selectedEmployeeData = {
      employeeId: selectedEmployee.id,
      employeeName: selectedEmployee.name,
      rowId: signeeRowId,
    };

    callbacks.coreActions.selectJsaSignatureEmployee(
      callbacks.jsaData,
      selectedEmployeeData,
      callbacks.emitPatch
    );

    callbacks.coreActions.updateJsaSignatureEmployeeSignature(
      callbacks.jsaData,
      signature,
      signeeRowId,
      callbacks.emitPatch
    );

    try {
      callbacks?.commit?.signatures?.(callbacks.jsaData, [
        {
          id: signeeRowId,
          employee_id: selectedEmployee.id || null,
          employee_sig:
            typeof signature === "string"
              ? signature.replace("data:image/png;base64,", "").trim()
              : null,
        },
      ]);
    } catch {}

    handleCloseModal();
  };

  const modalComponents = currentEmployee
    ? [
        <EmployeeSelectionModalInput
          key="employee"
          selectedEmployee={selectedEmployee}
          setSelectedEmployee={setSelectedEmployee}
          allEmployees={callbacks.allEmployees}
          disabled={disabled}
        />,
      ]
    : null;

  return (
    <div className={styles.signatureTableContainer}>
      <SignatureModal
        isOpen={modalState.signatureModalOpen}
        onClose={handleCloseModal}
        signee={modalState.currentSignee}
        existingSignature={modalState.currentSignatureImage}
        onSave={handleSave}
        onClear={() => {}}
        components={modalComponents}
      />

      <div className={styles.signatureCardList}>
        {callbacks.jsaData?.jsaEmployeeSignaturesInfo?.map((employee) => (
          <EmployeeSignatureRow
            key={employee.rowId}
            employee={employee}
            callbacks={callbacks}
            onEdit={handleOpenModal}
          />
        ))}
      </div>
      <div className={styles.signatureActionButtons}>
        <button
          className={styles.addButton}
          onClick={handleAutoAddEmployees}
          disabled={disabled}
        >
          Auto Add Employees
        </button>
        <button
          className={styles.addButton}
          onClick={handleAddEmployeeSignature}
          disabled={disabled}
        >
          Add Employee Signature
        </button>
      </div>
    </div>
  );
};

// Non-employee signature row
const NonEmployeeSignatureRow = ({ nonEmployee, callbacks, onEdit }) => {
  // Get row error for this non-employee signature
  const rowError = getSignatureRowError(callbacks.autosaveErrors, nonEmployee.rowId);
  const nameError = rowError?.non_employee_name;
  const companyError = rowError?.non_employee_company;

  // Handling opening the signature modal with a current non-employee signature
  const handleSignatureClick = () => {
    onEdit(nonEmployee.rowId);
  };

  // Handling removing a non-employee
  const handleRemoveNonEmployee = () => {
    callbacks.coreActions.removeJsaSignatureNonEmployeeRow(
      callbacks.jsaData,
      nonEmployee.rowId,
      callbacks.emitPatch
    );
  };

  return (
    <div className={`${styles.signatureCard} ${rowError ? styles.rowError : ""}`} key={nonEmployee.rowId}>
      <div className={styles.infoField}>
        <label className={styles.infoLabel}>Name</label>
        <div className={styles.infoValue}>
          {nonEmployee.nonEmployeeName || (
            <span className={styles.emptyValue}>No name provided</span>
          )}
        </div>
        {nameError && <span className={styles.errorText}>{nameError}</span>}
      </div>

      <div className={styles.infoField}>
        <label className={styles.infoLabel}>Company</label>
        <div className={styles.infoValue}>
          {nonEmployee.nonEmployeeCompany || (
            <span className={styles.emptyValue}>No company provided</span>
          )}
        </div>
        {companyError && <span className={styles.errorText}>{companyError}</span>}
      </div>

      <div
        className={styles.signatureDisplay}
        onClick={
          callbacks.jsaMode !== "view" ? handleSignatureClick : undefined
        }
      >
        {nonEmployee.nonEmployeeSig ? (
          <img
            src={nonEmployee.nonEmployeeSig}
            alt="Non-employee signature"
            className={styles.signatureImage}
          />
        ) : (
          <div className={styles.emptySignature}>
            <span>Click to sign</span>
          </div>
        )}
      </div>

      <div className={styles.signatureCardActions}>
        <button
          className={styles.signButton}
          onClick={handleSignatureClick}
          disabled={callbacks.jsaMode === "view"}
        >
          Edit
        </button>
        <button
          className={styles.deleteButton}
          onClick={handleRemoveNonEmployee}
          disabled={callbacks.jsaMode === "view"}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

// Non-employee signature table
const NonEmployeeSignatureTable = ({
  callbacks,
  modalState,
  setModalState,
}) => {
  const disabled = callbacks.jsaMode === "view";

  // Local state for modal inputs
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");

  // Get current non-employee being edited
  const currentNonEmployee =
    callbacks.jsaData?.jsaNonEmployeeSignaturesInfo?.find(
      (ne) => ne.rowId === modalState.currentSignee
    );

  // Update local state when modal opens with non-employee data
  useEffect(() => {
    if (modalState.signatureModalOpen && currentNonEmployee) {
      setName(currentNonEmployee.nonEmployeeName || "");
      setCompany(currentNonEmployee.nonEmployeeCompany || "");
    }
  }, [
    modalState.signatureModalOpen,
    modalState.currentSignee,
    currentNonEmployee,
  ]);

  // Handling adding a blank non-employee signature
  const handleAddBlankNonEmployeeSignature = () => {
    callbacks.coreActions.addBlankJsaNonEmployeeSignaturesInfoRow(
      callbacks.jsaData,
      callbacks.emitPatch
    );
    const rows = callbacks.jsaData?.jsaNonEmployeeSignaturesInfo || [];
    const newRow = rows[rows.length - 1];
    if (newRow) {
      // Open modal for new signature
      handleOpenModal(newRow.rowId);
    }
  };

  const handleOpenModal = (rowId) => {
    const nonEmployee = callbacks.jsaData?.jsaNonEmployeeSignaturesInfo?.find(
      (ne) => ne.rowId === rowId
    );
    setModalState({
      signatureModalOpen: true,
      currentSignee: rowId,
      currentSignatureImage: nonEmployee?.nonEmployeeSig || null,
    });
  };

  const handleCloseModal = () => {
    setModalState({
      signatureModalOpen: false,
      currentSignee: null,
      currentSignatureImage: null,
    });
    setName("");
    setCompany("");
  };

  const handleSave = (signature, signeeRowId) => {
    // Update non-employee info and signature
    callbacks.coreActions.updateJsaSignatureNonEmployeeName(
      callbacks.jsaData,
      name,
      signeeRowId,
      callbacks.emitPatch
    );

    callbacks.coreActions.updateJsaSignatureNonEmployeeCompany(
      callbacks.jsaData,
      company,
      signeeRowId,
      callbacks.emitPatch
    );

    callbacks.coreActions.updateJsaSignatureNonEmployeeSignature(
      callbacks.jsaData,
      signature,
      signeeRowId,
      callbacks.emitPatch
    );

    handleCloseModal();
  };

  const modalComponents = currentNonEmployee
    ? [
        <NonEmployeeNameInput
          key="name"
          name={name}
          setName={setName}
          disabled={disabled}
        />,
        <NonEmployeeCompanyInput
          key="company"
          company={company}
          setCompany={setCompany}
          disabled={disabled}
        />,
      ]
    : null;

  return (
    <div className={styles.signatureTableContainer}>
      <SignatureModal
        isOpen={modalState.signatureModalOpen}
        onClose={handleCloseModal}
        signee={modalState.currentSignee}
        existingSignature={modalState.currentSignatureImage}
        onSave={handleSave}
        onClear={() => {}}
        components={modalComponents}
      />

      <div className={styles.signatureCardList}>
        {callbacks.jsaData.jsaNonEmployeeSignaturesInfo.map((nonEmployee) => (
          <NonEmployeeSignatureRow
            key={nonEmployee.rowId}
            nonEmployee={nonEmployee}
            callbacks={callbacks}
            onEdit={handleOpenModal}
          />
        ))}
      </div>
      <div className={styles.signatureActionButtons}>
        <button
          className={styles.addButton}
          onClick={handleAddBlankNonEmployeeSignature}
          disabled={disabled}
        >
          Add Non-Employee Signature
        </button>
      </div>
    </div>
  );
};

// Tab buttons for the signature info tab, will be used to switch between the employee and non-employee signature tables
export const SignatureTabButtons = ({ tabName, callbacks }) => {
  // Handling updating the active signature tab
  const handleUpdateActiveSignatureTab = () => {
    callbacks.coreActions.updateJsaActiveSignatureTab(tabName);
  };

  return (
    <button
      className={`${styles.tabButton} ${
        callbacks.jsaCore.signatureInfo.jsaActiveSignatureTab === tabName
          ? styles.active
          : ""
      }`}
      onClick={handleUpdateActiveSignatureTab}
    >
      {tabName}
    </button>
  );
};

// Main signature info component with tabs
export const JsaSignatureInfoTab = ({ callbacks }) => {
  // Local modal state management
  const [modalState, setModalState] = useState({
    signatureModalOpen: false,
    currentSignee: null,
    currentSignatureImage: null,
  });

  // If we are using the samplingEventForm in our callbacks, we will need to check this argument that is passed from the JsaTab
  const usingSamplingEventForm = callbacks.samplingEventForm;
  if (usingSamplingEventForm) {
    return (
      <div className={styles.jsaSignatureInfoContainer}>
        <EmployeeSignatureTable
          callbacks={callbacks}
          modalState={modalState}
          setModalState={setModalState}
        />
      </div>
    );
  } else {
    return (
      <div className={styles.jsaSignatureInfoContainer}>
        <div className={styles.signatureTabMenuBar}>
          <SignatureTabButtons
            tabName="Employee Signatures"
            callbacks={callbacks}
          />
          <SignatureTabButtons
            tabName="Non-Employee Signatures"
            callbacks={callbacks}
          />
        </div>

        {/* Switch case for the signature tables */}
        {(() => {
          switch (callbacks.jsaCore.signatureInfo.jsaActiveSignatureTab) {
            case "Employee Signatures":
              return (
                <EmployeeSignatureTable
                  callbacks={callbacks}
                  modalState={modalState}
                  setModalState={setModalState}
                />
              );
            case "Non-Employee Signatures":
              return (
                <NonEmployeeSignatureTable
                  callbacks={callbacks}
                  modalState={modalState}
                  setModalState={setModalState}
                />
              );
            default:
              return null;
          }
        })()}
      </div>
    );
  }
};
